/**
 * Mobile Menu Fullscreen con Blur
 * Custom element: <mobile-menu>
 * Controla apertura/cierre, submenus expandibles, focus trap, body lock
 */

if (!customElements.get('mobile-menu')) {
  class MobileMenu extends HTMLElement {
    constructor() {
      super();
      this.menuToggle = document.querySelector('[data-menu-toggle]');
      this.menuClose = this.querySelector('[data-menu-close]');
      this.menuOverlay = this.querySelector('[data-menu-overlay]');
      this.submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
      this.scrollPos = 0;
    }

    connectedCallback() {
      /* Abrir */
      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', () => this.open());
      }

      /* Cerrar — botón, overlay, ESC */
      if (this.menuClose) {
        this.menuClose.addEventListener('click', () => this.close());
      }
      if (this.menuOverlay) {
        this.menuOverlay.addEventListener('click', () => this.close());
      }
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('active')) {
          this.close();
        }
      });

      /* Submenus expandibles */
      this.submenuTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.toggleSubmenu(trigger));
      });

      this.querySelectorAll('[data-mobile-search-form]').forEach(form => {
        form.addEventListener('submit', (e) => this.submitSearch(e, form));
        form.querySelectorAll('[data-mobile-search-submit]').forEach(button => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.submitSearch(e, form);
          });
        });
        form.addEventListener('click', (e) => {
          if (e.target.closest('button, input')) return;
          const input = form.querySelector('input[name="q"]');
          if (input) input.focus();
        });
      });

      this.querySelectorAll('[data-mobile-newsletter-form], .mobile-menu__newsletter-form').forEach(form => {
        this.setupNewsletterForm(form);
      });

      /* Cerrar al cambiar orientación */

      window.addEventListener('orientationchange', () => {
        if (this.classList.contains('active')) this.close();
      });
    }

    open() {
      this.scrollPos = window.scrollY;
      this.classList.add('active');
      document.body.classList.add('mobile-menu-open');
      document.body.style.top = `-${this.scrollPos}px`;

      if (this.menuToggle) {
        this.menuToggle.setAttribute('aria-expanded', 'true');
      }

      this.trapFocus();
      document.dispatchEvent(new CustomEvent('mobile-menu:opened'));
    }

    close() {
      this.classList.remove('active');
      this.querySelectorAll('.mobile-menu__item.expanded, .mobile-menu__submenu-item.expanded').forEach(item => item.classList.remove('expanded'));
      this.querySelectorAll('[data-submenu-trigger]').forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('mobile-menu-open');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollPos);

      if (this.menuToggle) {
        this.menuToggle.setAttribute('aria-expanded', 'false');
      }

      this.releaseFocus();
      document.dispatchEvent(new CustomEvent('mobile-menu:closed'));
    }

    toggleSubmenu(trigger) {
      const parentItem = trigger.closest('.mobile-menu__item, .mobile-menu__submenu-item');
      if (!parentItem) return;

      const isExpanded = parentItem.classList.contains('expanded');

      /* Close only siblings at the same level */
      const parentList = parentItem.parentElement;
      if (parentList) {
        Array.prototype.slice.call(parentList.children).forEach(sib => {
          if (sib !== parentItem && sib.classList && sib.classList.contains('expanded')) {
            sib.classList.remove('expanded');
            const sibTrigger = sib.querySelector(':scope > [data-submenu-trigger]');
            if (sibTrigger) sibTrigger.setAttribute('aria-expanded', 'false');
          }
        });
      }

      parentItem.classList.toggle('expanded');
      trigger.setAttribute('aria-expanded', !isExpanded);
    }

    submitSearch(event, form) {
      event.preventDefault();
      event.stopPropagation();
      const input = form.querySelector('input[name="q"]');
      const query = input ? input.value.trim() : '';
      if (!query) {
        if (input) input.focus();
        return;
      }
      const action = form.getAttribute('action') || '/search';
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      window.location.href = `${action}?${params.toString()}`;
    }

    setupNewsletterForm(form) {
      if (form.__mobileMenuNewsletterInit) return;
      form.__mobileMenuNewsletterInit = true;

      const frameName = `${form.getAttribute('id') || 'mobile-menu-newsletter'}-frame`;
      let frame = document.querySelector(`iframe[name="${frameName}"]`);

      if (!frame) {
        frame = document.createElement('iframe');
        frame.name = frameName;
        frame.setAttribute('title', 'Newsletter subscription');
        frame.hidden = true;
        frame.style.display = 'none';
        (form.parentNode || document.body).appendChild(frame);
      }

      form.setAttribute('target', frameName);

      const showNewsletterSuccess = () => {
        const row = form.querySelector('[data-mobile-newsletter-row]');
        const success = form.querySelector('[data-mobile-newsletter-success]');
        const error = form.querySelector('[data-mobile-newsletter-error]');
        const button = form.querySelector('button[type="submit"]');

        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
        if (row) row.hidden = true;
        if (success) {
          success.textContent = 'Gracias por suscribirte.';
          success.hidden = false;
          success.removeAttribute('hidden');
        }
        form.reset();
        form.__mobileMenuNewsletterSubmitting = false;
        if (button) button.disabled = false;

        window.clearTimeout(form.__mobileMenuNewsletterSuccessTimer);
        form.__mobileMenuNewsletterSuccessTimer = window.setTimeout(() => {
          if (success) success.hidden = true;
          if (row) row.hidden = false;
        }, 3000);
      };

      frame.addEventListener('load', () => {
        if (!form.__mobileMenuNewsletterSubmitting) return;
        showNewsletterSuccess();
      });

      form.addEventListener('submit', () => {
        const email = form.querySelector('input[type="email"]');
        if (email && typeof email.checkValidity === 'function' && !email.checkValidity()) return;

        form.__mobileMenuNewsletterSubmitting = true;

        const button = form.querySelector('button[type="submit"]');
        const error = form.querySelector('[data-mobile-newsletter-error]');

        window.setTimeout(() => {
          if (button) button.disabled = true;
        }, 0);
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
      });
    }

    trapFocus() {
      const focusable = this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      this._focusTrap = (e) => {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      };

      document.addEventListener('keydown', this._focusTrap);
      setTimeout(() => first.focus(), 100);
    }

    releaseFocus() {
      if (this._focusTrap) {
        document.removeEventListener('keydown', this._focusTrap);
      }
      if (this.menuToggle) this.menuToggle.focus();
    }
  }

  customElements.define('mobile-menu', MobileMenu);
}
